export const setProgrammeDefaults = (original) => {
    if (original) {
        const withDefaults =
        {
            ...original,
            isRadio: original?.isRadio == null ? false : original.isRadio
        }

        return withDefaults
    }
    return undefined
}

const resequenceDraftTransmissions = (transmissions, originalTransmissions) => {
    const originalSlns = new Set(
        Object.values(originalTransmissions || {}).map(t => t.sapLineNum)
    )
    const maxOriginalSln = originalSlns.size > 0 ? Math.max(...originalSlns) : 0

    const originalRows = transmissions.filter(t => originalSlns.has(t.sapLineNum))
    const draftOnlyRows = transmissions
        .filter(t => !originalSlns.has(t.sapLineNum))
        .sort((a, b) => a.sapLineNum - b.sapLineNum)

    let nextSln = maxOriginalSln + 1
    const resequenced = draftOnlyRows.map(t => ({ ...t, sapLineNum: nextSln++ }))

    return [...originalRows, ...resequenced]
}

export const mergeChanges = (original, draft, episodeOriginal, episodeDraft) => {
    if (original) {
        const mergedTitles = draft?.titles || original?.titles || {}
        const mergedCountries = {
            ...original.countries,
            ...draft?.countries || []
        }
        const mergedProductionCompanies = {
            ...original.productionCompanies,
            ...draft?.productionCompanies || []
        }
        const mergedEpisodesList = {
            ...original.episodes,
            ...draft?.episodes || []
        }
        const draftEpisode = mergeEpisodeChanges(episodeOriginal, episodeDraft)

        // Merge the draft episode into the original episodes list
        const mergedEpisodes = Object.entries(mergedEpisodesList)
            .reduce((obj, [key, episode]) => {
                obj[key] = episode.id === draftEpisode.id
                    ? draftEpisode
                    : episode
                return obj
            }, {})
        // If the draft episode is new, add it to the list
        if (draftEpisode && Object.keys(draftEpisode).length > 0) {          
            const isDraftEpisodeInOriginal = Object.values(original.episodes).some(episode => episode.id === draftEpisode.id)
            // If draftEpisode is not found in original.episodes, add it to mergedEpisodes
            if (!isDraftEpisodeInOriginal) {
                mergedEpisodes[draftEpisode.id] = draftEpisode
            }
        }
        else {
            console.warn('draftEpisode or draftEpisode.id is undefined')
        }

        const filteredCountries = Object.entries(mergedCountries)
            .filter(([key, country]) => !country.isDeleted)
            .reduce((obj, [key, country]) => {
                obj[key] = country
                return obj
            }, {})

        const filteredProductionCompanies = Object.entries(mergedProductionCompanies)
            .filter(([key, company]) => !company.isDeleted)
            .reduce((obj, [key, company]) => {
                obj[key] = company
                return obj
            }, {})

        const filteredEpisodes = Object.entries(mergedEpisodes)
            .filter(([key, episode]) => !episode.isDeleted)
            .reduce((obj, [key, episode]) => {
                obj[key] = episode
                return obj
            }, {})

        return {
            ...original,
            ...draft,
            isRadio: draft?.isRadio ?? original.isRadio,
            titles: mergedTitles,
            countries: filteredCountries,
            productionCompanies: filteredProductionCompanies,
            episodes: filteredEpisodes
        }
    }
    return {}
}

export const mergeEpisodeChanges = (original, draft) => {
    if (original) {
        const mergedTitles = draft?.titles || original?.titles || {}

        // Merge transmissions - spread merge, then fix entries where both exist
        const mergedTransmissions = {
            ...original.transmissions,
            ...draft?.transmissions || {}
        }
        // Fix entries where both original and draft exist (spread overwrote, need to merge instead)
        Object.keys(draft?.transmissions || {}).forEach(key => {
            if (original.transmissions?.[key]) {
                mergedTransmissions[key] = { ...original.transmissions[key], ...draft.transmissions[key] }
            }
        })

        // sapLineNum is already correctly set by generateSapLineNum in the grid layer
        // Draft key and transmission.sapLineNum are always the same value
        const updatedTransmissions = { ...mergedTransmissions }

        // Merge contributions by id - draft updates should be merged with original to preserve data
        const mergedContributions = Object.values(original.contributions || {}).reduce((acc, contribution) => {
            const id = contribution.id
            // If there's a draft update for this contribution, merge it with original to preserve all data
            acc[id] = draft?.contributions?.[id]
                ? { ...contribution, ...draft.contributions[id] }
                : contribution
            return acc
        }, {})
        // Add any new contributions from draft that don't exist in original
        if (draft?.contributions) {
            Object.entries(draft.contributions).forEach(([id, contribution]) => {
                if (!mergedContributions[id]) {
                    mergedContributions[id] = contribution
                }
            })
        }
        // Keep deleted items in presentation (for red/italic display) - filter only when sending to API
        return {
            ...original,
            ...draft,
            sapCode: draft?.sapCode ?? original.sapCode,
            titles: mergedTitles,
            transmissions: updatedTransmissions,
            contributions: mergedContributions
        }
    }
    return {}
}

export const mergeEpisodeListChanges = (original, draft) => {
    if ( original && original.length > 0 ) {
        const listOriginal = original.reduce((acc, episode) => {
            acc[episode.episodeId] = episode
            return acc
        }, {})

        if (draft) {
            const draftArray = Object.values(draft)
            const listDraft = draftArray.reduce((acc, episode) => {
                acc[episode.episodeId] = episode
                return acc
            }, {})

            // Merge the original list with the draft changes
            const merged = {
                ...listOriginal,
                ...listDraft
            }

            // Convert the merged object back to an array
            return merged
        }
        return listOriginal
      
    }
    return {}   
}



export const mergeEpisodeChangesOnSave = (programmeOriginal, episodeOriginal) => {
    if (!programmeOriginal || !episodeOriginal) {
        return {}
    }

    // Derive title without mutating the source object
    let episodeTitle = episodeOriginal.title
    if (!episodeTitle && Array.isArray(episodeOriginal.titles)) {
        const mainTitle = episodeOriginal.titles.find(title => title.titleTypeId === 1)
        if (mainTitle) {
            episodeTitle = mainTitle.title
        }
    }

    // Episodes list now uses AudioVisualResultModel field names
    // Map from episode detail field names to list field names
    const episodeId = episodeOriginal.id

    // Build merged episodes - normalize to array (may be object keyed by episodeId from Redux state)
    const rawEpisodes = programmeOriginal.episodes ?? []
    const episodesArray = Array.isArray(rawEpisodes) ? rawEpisodes : Object.values(rawEpisodes)
    const episodesById = episodesArray.reduce((acc, ep) => {
        acc[ep.episodeId] = ep
        return acc
    }, {})

    // Map episode detail fields to episode list fields
    const episodeListData = {
        episodeId: episodeId,
        episodeTitle: episodeTitle,
        episodeDuration: episodeOriginal.durationMinutes,
        episodeNumber: episodeOriginal.number,
        episodeSeries: episodeOriginal.series,
        episodeYear: episodeOriginal.yearOfProduction,
        episodeIda: episodeOriginal.idaCode,
        id: episodeId,
        episodeCode: episodeOriginal.sapCode
    }

    if (episodesById[episodeId]) {
        // Update the existing episode with new field names
        episodesById[episodeId] = {
            ...episodesById[episodeId],
            ...episodeListData
        }
    } else {
        // Add new episode to the list
        episodesById[episodeId] = episodeListData
    }

    // Convert back to array
    const mergedEpisodes = Object.values(episodesById)

    // Update episodesCount to reflect actual count
    const newEpisodesCount = mergedEpisodes.length

    return {
        ...programmeOriginal,
        episodes: mergedEpisodes,
        episodesCount: newEpisodesCount
    }
}

export const mergeEpisodeListChangesOnSave = (programmeOriginal, episodeOriginal) => {
    if (!programmeOriginal || !episodeOriginal) {
        return
    }

    const updatedEpisode = { ...episodeOriginal }

    // Episodes list now uses AudioVisualResultModel field names (episodeId, episodeTitle, etc.)
    // Normalize to array (may be object keyed by episodeId from Redux state)
    const rawEpisodes = programmeOriginal.episodes ?? []
    const episodesArray = Array.isArray(rawEpisodes) ? rawEpisodes : Object.values(rawEpisodes)
    const programmeEpisode = episodesArray.find(ep => ep.episodeId === episodeOriginal.id)

    if (programmeEpisode) {
        // Map from list field names (AudioVisualResultModel) to detail field names (EpisodeModel)
        updatedEpisode.title = programmeEpisode.episodeTitle
        updatedEpisode.durationMinutes = programmeEpisode.episodeDuration
        updatedEpisode.idaCode = programmeEpisode.episodeIda ? `${programmeEpisode.episodeIda}` : ""
        updatedEpisode.yearOfProduction = programmeEpisode.episodeYear
        updatedEpisode.number = programmeEpisode.episodeNumber
        updatedEpisode.series = programmeEpisode.episodeSeries
    }

    return updatedEpisode
}


export const transformDraftToProgrammeInfo = (draft, original, episodeOriginal, episodeDraft, programmeOnlyIndicator) => {

    //TODO: Need to send only the changed fields; delta

    var presentation = mergeChanges(original, draft, episodeOriginal, episodeDraft)

    // Check if formatId is 1 or 5
    const isFilm = presentation.formatId === 1 || presentation.formatId === 5

    const transformed = {
        AnimeOrLive: presentation.isRadio ? "" : (presentation?.animeOrLive?.id || presentation?.animeOrLive || "LI"),
        FormatId: presentation.formatId,
        IsRadio: presentation.isRadio,
        LanguageId: presentation.languageId.id? presentation.languageId.id : presentation.languageId,
        ProgrammeTypeId: presentation.programmeTypeId,
        SapCode: presentation.sapCode? presentation.sapCode : "",
        Comment: presentation.comment,
        IdAId: presentation.idAid,
        TotalEpisodes: presentation.totalEpisodes,
        ProductionCompanies: Object.values(presentation.productionCompanies).map(company => company.companyId),
        Countries: Object.values(presentation.countries).map(country => ({
            CountryId: country.countryId,
            IsPrimary: country.isPrimary
        })),
        Titles: Object.values(presentation.titles).map(title => ({
            Title: title.title,
            LanguageId: title.languageId,
            TitleTypeId: title.titleTypeId
        })),
        EpisodeInfos: (isFilm || programmeOnlyIndicator) ? Object.values(presentation.episodes).map(episode => ({
            Id: episode.id,
            DurationMinutes: episode.durationMinutes,
            IdaCode: episode.idaCode,
            YearOfProduction: episode.yearOfProduction,
            Number: isFilm ? 0 : episode.number,
            Series: isFilm ? 0 : episode.series,
            SapCode: episode.sapCode,
            Comments: episode.comments,
            ProgrammeSapCode: episode.programmeSapCode,
            Titles: isFilm ? Object.values(presentation.titles).filter(title => title.titleTypeId === 1).map(title => ({
                Title: title.title,
                LanguageId: title.languageId,
                TitleTypeId: title.titleTypeId
            })) :
                Object.values(episode.titles ?? {}).map(title => ({
                Title: title.title,
                LanguageId: title.languageId,
                TitleTypeId: title.titleTypeId ? title.titleTypeId : 2
            })),
            Transmissions: resequenceDraftTransmissions(
                Object.values(episode.transmissions ?? {}).filter(t => !t.isDeleted),
                episode.id === episodeOriginal?.id ? episodeOriginal?.transmissions : {}
            ).map(transmission => ({
                sapLineNum: transmission.sapLineNum,
                EpisodeId: episode.id,
                ChannelId: transmission.channelId,
                Date: transmission.date
            })),
            Contributions: Object.values(episode.contributions ?? {}).filter(c => !c.isDeleted).map(contribution => ({
                Id: contribution.id,
                EpisodeId: episode.id,
                ContactId: contribution.contactId,
                ContactNameLineNum: contribution.contactNameLineNum,
                Type: contribution.contributionTypeId,
                SapCode: contribution.sapCode,
                Minutes: contribution.minutes,
                Percentage: contribution.percentage,
                IsPublicDomain: contribution.isPublicDomain || false,
                IsExcluded: contribution.isExcluded || false,
                MembershipLineNum: contribution.membershipLineNum ?? null
            }))
        })) : []

    }
    return transformed
}


export const transformEpisodeDraftToEpisodeInfo = (episodeOriginal, episodeDraft) => {

    //TODO: Need to send only the changed fields; delta

    var presentation = mergeEpisodeChanges(episodeOriginal, episodeDraft)

    const transformed = {
        Id: presentation.id,
        DurationMinutes: presentation.durationMinutes,
        IdaCode: presentation.idaCode,
        YearOfProduction: presentation.yearOfProduction,
        Number: presentation.number,
        Series: presentation.series,
        SapCode: presentation.sapCode,
        Comments: presentation.comments,
        ProgrammeSapCode: presentation.programmeSapCode,
        Titles: Object.values(presentation.titles).map(title => ({
            Title: title.title,
            LanguageId: title.languageId,
            TitleTypeId: title.titleTypeId ? title.titleTypeId : 2
        })),
        Transmissions: resequenceDraftTransmissions(
            Object.values(presentation.transmissions).filter(t => !t.isDeleted),
            episodeOriginal?.transmissions
        ).map(transmission => ({
            sapLineNum: transmission.sapLineNum,
            EpisodeId: presentation.id,
            ChannelId: transmission.channelId,
            Date: transmission.date
        })),
        Contributions: Object.values(presentation.contributions || {}).filter(c => !c.isDeleted).map(contribution => ({
            Id: contribution.id,
            EpisodeId: presentation.id,
            ContactId: contribution.contactId,
            ContactNameLineNum: contribution.contactNameLineNum,
            Type: contribution.contributionTypeId,
            SapCode: contribution.sapCode,
            Minutes: contribution.minutes,
            Percentage: contribution.percentage,
            IsPublicDomain: contribution.isPublicDomain || false,
            IsExcluded: contribution.isExcluded || false,
            MembershipLineNum: contribution.membershipLineNum ?? null
        }))
    }
    return transformed
}

export const transformEpisodeListDraftToEpisodeInfos = (episodeDraft) => {
    // Ensure episodeDraft is an object and transform it into an array of episode objects
    // Uses AudioVisualResultModel field names: episodeId, episodeTitle, episodeDuration, etc.
    const transformed = Object.values(episodeDraft).map(episode => ({
        Id: episode.episodeId,
        Title: episode.episodeTitle,
        DurationMinutes: episode.episodeDuration,
        IdaCode: episode.episodeIda ? `${episode.episodeIda}` : "",
        YearOfProduction: episode.episodeYear,
        Number: episode.episodeNumber,
        Series: episode.episodeSeries
    }))

    return transformed
}

export const transformProgrammeEpisodeListDraftToEpisodeInfos = (episodeDraft) => {   
    const episodesArray = Object.values(episodeDraft).map(episode => ({
        Id: episode.episodeId,
        ProgrammeId: episode.programmeId, 
        Title: episode.episodeTitle,
        DurationMinutes: episode.episodeDuration,
        IdaCode: episode.episodeIda ? `${episode.episodeIda}` : "",
        YearOfProduction: episode.episodeYear,
        Number: episode.episodeNumber,
        Series: episode.episodeSeries,
        Version:episode.version
    }))

    // Group episodes by programmeId
    const groupedByProgrammeId = episodesArray.reduce((acc, episode) => {
        if (!acc[episode.ProgrammeId]) {
            acc[episode.ProgrammeId] = []
        }
        acc[episode.ProgrammeId].push(episode)
        return acc
    }, {})
    // Create a list of ProgrammeEpisodeListInfo objects
    const programmeEpisodeListInfos = Object.entries(groupedByProgrammeId).map(([programmeId, episodes]) => {
        //all episodes in the same programme have the same version
        const version = episodes[0].Version

        // Remove Version from each episode
        episodes.forEach(episode => {
            delete episode.Version
            delete episode.ProgrammeId
        })

        return {
            Id: programmeId,
            Version: version,
            EpisodeInfos: episodes
        }
    })

    return programmeEpisodeListInfos
}
